import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { floatingHostFor, promoteToTopLayer, type FloatingHost } from '@/lib/topLayer';
import { cn } from '@/lib/utils';

export interface MenuItem {
  /** Stable key — also the React key for the row. */
  key: string;
  label: string;
  /** A second line under the label, where the name alone is not enough. */
  description?: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** `danger` draws the row in the critical tone — suspending, archiving. */
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export interface MenuProps {
  /** What is drawn inside the trigger button. */
  trigger: ReactNode;
  items: MenuItem[];
  /** Names the menu for screen readers — "More actions for Class 1". */
  label: string;
  /** Which edge of the trigger the panel lines up with. */
  align?: 'start' | 'end';
  triggerClassName?: string;
  panelClassName?: string;
  disabled?: boolean;
}

/** The air kept between the panel and its trigger. */
const TRIGGER_GAP = 6;

/** And between the panel and the edge of the window, on every side. */
const VIEWPORT_EDGE = 12;

/**
 * The product's dropdown menu — a list of actions hanging off a button.
 *
 * Separate from `Select` on purpose: a select answers "which one", a menu says
 * "do this". They look alike and behave differently, and a listbox pretending to
 * be a menu announces itself wrongly to a screen reader.
 *
 * The panel is portalled to `document.body` and positioned against the trigger's
 * box, because the two places this is used — a toolbar button and the last cell
 * of a table row — both sit inside containers that clip and scroll. Anything
 * drawn in flow there is cut off at the container's edge.
 */
export function Menu({
  trigger,
  items,
  label,
  align = 'end',
  triggerClassName,
  panelClassName,
  disabled = false,
}: MenuProps) {
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);
  /** Where the panel is drawn, which a modal on the screen changes. */
  const [host, setHost] = useState<FloatingHost>(() => ({
    container: document.body,
    needsTopLayer: false,
  }));

  /** Keeps our handle on the panel, and lifts it clear of an open dialog. */
  const attachPanel = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (host.needsTopLayer) promoteToTopLayer(node);
    },
    [host.needsTopLayer],
  );

  const enabledIndexes = items.flatMap((item, index) => (item.disabled ? [] : [index]));

  function open() {
    if (disabled || items.length === 0) return;
    setActiveIndex(enabledIndexes[0] ?? 0);
    // Asked each time rather than once: the same menu is drawn on a page and
    // inside a dialog, and only the second needs the top layer.
    setHost(floatingHostFor(triggerRef.current));
    setIsOpen(true);
  }

  function close({ restoreFocus = true }: { restoreFocus?: boolean } = {}) {
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function choose(item: MenuItem) {
    if (item.disabled) return;
    close();
    item.onSelect();
  }

  /** Steps to the next row that can actually be chosen, wrapping at both ends. */
  function moveActive(delta: number) {
    if (enabledIndexes.length === 0) return;
    setActiveIndex((current) => {
      const at = enabledIndexes.indexOf(current);
      const next = (at + delta + enabledIndexes.length) % enabledIndexes.length;
      return enabledIndexes[at === -1 ? 0 : next];
    });
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(enabledIndexes[0] ?? 0);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? 0);
        return;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const item = items[activeIndex];
        if (item) choose(item);
        return;
      }
      case 'Escape':
        event.preventDefault();
        close();
        return;
      case 'Tab':
        // Let focus leave naturally, but never leave the panel hanging open.
        close({ restoreFocus: false });
    }
  }

  /**
   * Anchored to the trigger's box rather than nested inside it, from the panel's
   * own measured size.
   *
   * Measured rather than shifted with a `translateX(-100%)`: the pop-in keyframes
   * animate `transform`, and an animation outranks an inline style in the
   * cascade, so the shift was dropped the moment the panel appeared. An
   * `end`-aligned menu in the last cell of a table then opened *past* the right
   * edge of the window, which read as "the menu does not open".
   *
   * Both axes are then held inside the viewport, and the panel flips above its
   * trigger when the room below has run out — the last row of a long table is
   * exactly where a menu is most likely to be cut off.
   */
  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;

    const left = Math.max(
      VIEWPORT_EDGE,
      Math.min(
        align === 'end' ? trigger.right - panel.width : trigger.left,
        window.innerWidth - panel.width - VIEWPORT_EDGE,
      ),
    );

    const below = trigger.bottom + TRIGGER_GAP;
    const above = trigger.top - TRIGGER_GAP - panel.height;
    const fitsBelow = below + panel.height <= window.innerHeight - VIEWPORT_EDGE;
    const top = fitsBelow ? below : Math.max(VIEWPORT_EDGE, above);

    setBox({ top, left });
  }, [isOpen, align, items.length]);

  // Measured on every open, so a stale box from the last one is never used to
  // draw the panel a frame before the effect above corrects it.
  useEffect(() => {
    if (!isOpen) setBox(null);
  }, [isOpen]);

  // A panel positioned in viewport coordinates drifts away from its trigger the
  // moment anything outside scrolls, so it closes instead.
  useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event: Event) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    const handleResize = () => setIsOpen(false);

    window.addEventListener('scroll', dismiss, { passive: true, capture: true });
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', dismiss, { capture: true });
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // No focus restore: the pointer has already chosen where to go, and
      // yanking focus back to the trigger would fight it.
      setIsOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        aria-label={label}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        className={cn(
          'inline-flex items-center justify-center rounded-field transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
          'disabled:cursor-not-allowed disabled:opacity-55',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={attachPanel}
            id={menuId}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            // Drawn before it is placed so it can be measured, and kept out of
            // sight for that one frame — the effect above runs before paint.
            style={{
              top: box?.top ?? 0,
              left: box?.left ?? 0,
              visibility: box ? 'visible' : 'hidden',
            }}
            className={cn(
              'fixed z-50 min-w-[13rem] max-w-[min(20rem,calc(100vw-1.5rem))]',
              'max-h-[min(24rem,calc(100vh-1.5rem))] overflow-y-auto',
              'rounded-card border border-line bg-surface p-1 shadow-card',
              'animate-pop-in focus:outline-none',
              panelClassName,
            )}
          >
            {items.map((item, index) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                tabIndex={-1}
                onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                onClick={() => choose(item)}
                className={cn(
                  'flex w-full items-start gap-2.5 rounded-field px-2.5 py-2 text-left transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  item.tone === 'danger' ? 'text-crit' : 'text-ink',
                  index === activeIndex &&
                    !item.disabled &&
                    (item.tone === 'danger' ? 'bg-crit-bg' : 'bg-surface-2'),
                )}
              >
                {item.icon && (
                  <span className="mt-0.5 shrink-0" aria-hidden>
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-[560]">{item.label}</span>
                  {item.description && (
                    <span className="mt-0.5 block text-xs text-ink-3">{item.description}</span>
                  )}
                </span>
              </button>
            ))}
          </div>,
          host.container,
        )}
    </>
  );
}
